import PlayMusicButton from "./play-music-button/PlayMusicButton";
import { useState, useEffect } from "react";
import useSound from "use-sound";
import { Card } from "components/ui/card";
import { Service } from "utils/services";
import {
  AudioLines,
  Camera,
  Pause,
  Play,
  Scissors,
  Upload,
} from "lucide-react";
import { logoFlipFlop } from "assets";
import DialogTrimMusic from "./DialogTrimMusic";
import UploadMusicPicture from "components/app/upload-profile/UploadMusicPicture";
import DialogUploadSoundMusic from "./DialogUploadSoundMusic";
import DialogShareReviewMusic from "./DialogShareReviewMusic";
import DialogSendReviewMusicCopyright from "./DialogSendReviewMusicCopyright";
import {
  setAudioRef,
  setCurrentlyPlayingMusicData,
  setIsPlaying,
} from "store/musicLibraries";
import { useDispatch } from "react-redux";
import { useAppSelector } from "store/store";
import DialogSendSharedMusic from "./DialogSendSharedMusic";

interface CardMusicItemMusicLibrariesProps {
  musicData: any;
  setItemMusicClicked: (item: any) => void;
  setMusicDatas: (value: any) => void;
}

export default function CardMusicItemMusicLibraries({
  musicData,
  setItemMusicClicked,
  setMusicDatas,
}: CardMusicItemMusicLibrariesProps) {
  const dispatch = useDispatch();
  const { isPlaying, currentlyPlayingMusicData } = useAppSelector(
    (state) => state.musicLibraries
  );
  const [isDialogTrimMusic, setIsDialogTrimMusic] = useState<boolean>(false);
  const [isDialogSendSharedMusic, setIsDialogSendSharedMusic] =
    useState<boolean>(false);
  const [isDialogShareMusic, setIsDialogShareMusic] = useState<boolean>(false);

  const [
    isDialogShareSendReviewMusicCopyright,
    setIsDialogShareSendReviewMusicCopyright,
  ] = useState<boolean>(false);
  const [isDialogUploadSoundMusic, setIsDialogUploadSoundMusic] =
    useState<boolean>(false);
  const [initMusic, setInitMusic] = useState<any>();

  useEffect(() => {
    setInitMusic(initMusic);
  }, [initMusic]);

  const musicStatus = (musicStatus: string) => {
    if (musicStatus === "New") {
      return "bg-green-600";
    } else if (musicStatus === "In Review") {
      return "bg-yellow-500";
    } else if (musicStatus === "No") {
      return "bg-red-600";
    } else if (musicStatus === "Approved") {
      return "bg-green-600";
    }
  };

  const handleShareClick = (e: React.MouseEvent, shareStatus: string) => {
    e.stopPropagation();
    if (shareStatus === "No") {
      setIsDialogShareMusic(true);
    } else if (shareStatus === "Yes") {
      setIsDialogSendSharedMusic(true);
    }
  };

  const initStatusColors = (value: string) => {
    let color;
    if (value === "In Review") {
      color = "bg-yellow-500";
    } else if (value === "No") {
      color = "bg-green-600";
    }

    return color;
  };

  return (
    <Card className="p-2 rounded-md w-full cursor-pointer me-2 relative">
      <div
        className={`absolute w-[100px] text-center top-0 right-0 rounded-tr-md rounded-bl-md px-2 pb-0.5 text-xs font-medium text-white ${
          musicData.inactived === "Yes" ? "bg-red-600" : ""
        } ${musicStatus(musicData?.approved)}`}
      >
        {musicData.inactived === "Yes" ? (
          "Inactive"
        ) : (
          <>
            {musicData?.approved === "Yes"
              ? ""
              : musicData?.approved === "No"
              ? "Unpublished"
              : musicData?.approved}
          </>
        )}
      </div>
      <div className="flex h-full cursor-pointer gap-3 items-center">
        {/* Image with Play Button Centered */}
        <div className="relative">
          <div className="flex h-full justify-center rounded-bl-md rounded-tl-md w-full items-center">
            {musicData.thumbnail ? (
              <img
                src={musicData.thumbnail}
                className="h-full rounded-bl-md rounded-tl-md w-16 object-cover"
                alt="song"
              />
            ) : (
              <div className="flex h-full justify-center w-12 items-center">
                <img src={logoFlipFlop} className="object-cover" alt="logo" />
              </div>
            )}
          </div>

          <div className="bg-slate-500 p-1 rounded-full -translate-x-1/2 -translate-y-1/2 absolute left-1/2 opacity-50 top-1/2 z-50">
            <UploadMusicPicture
              soundItem={musicData}
              onUploadSuccess={(valueSuccess: any) => {
                setMusicDatas((prevState: any) => ({
                  ...prevState,
                  songs: {
                    ...prevState.songs,
                    data: prevState?.songs?.data?.map((el: any) =>
                      el.id === valueSuccess.id
                        ? { ...el, ...valueSuccess }
                        : el
                    ),
                  },
                }));
              }}
            />
          </div>
        </div>

        {/* Sound Details */}
        <div
          onClick={() => {
            setItemMusicClicked(musicData);
          }}
          className="flex-1"
        >
          <p className="text-md font-semibold line-clamp-1">
            {musicData?.sound_title}
          </p>
          <div className="flex justify-between w-full">
            <div>
              <p className="text-muted-foreground text-xs font-semibold line-clamp-1">
                {musicData?.sound_singer ? musicData.sound_singer : "Unknown"} •{" "}
                {musicData?.sound_genres ? musicData.sound_genres : "None"} •{" "}
                {Service.formatSoundDuration(musicData.sound_duration)}{" "}
                <span>• {musicData?.usage_count} Used</span>
              </p>
              <p className="text-muted-foreground text-sm font-semibold line-clamp-1">
                {musicData?.hashtag}
              </p>
            </div>

            {/* <div
              className=""
              onClick={(e) => {
                e.stopPropagation();
                setIsDialogTrimMusic(true);
              }}
            >
              <Scissors />
            </div> */}
          </div>

          <div className="flex justify-between">
            <div className="flex items-center space-x-2">
              <button
                className={`flex items-center justify-center px-1 py-1 text-white rounded-full bg-slate-500 opacity-65`}
                onClick={(e) => {
                  e.stopPropagation();
                  // handlePlayPause();
                }}
              >
                {musicData.sound_url ? (
                  <>
                    {currentlyPlayingMusicData?.id === musicData?.id ? (
                      <>
                        {isPlaying ? (
                          <Pause
                            onClick={() => {
                              dispatch(setCurrentlyPlayingMusicData(musicData));
                            }}
                            size={18}
                          />
                        ) : (
                          <Play
                            onClick={() => {
                              dispatch(setCurrentlyPlayingMusicData(musicData));
                              dispatch(setIsPlaying(true));
                            }}
                            size={18}
                          />
                        )}
                      </>
                    ) : (
                      <Play
                        onClick={() => {
                          dispatch(setCurrentlyPlayingMusicData(musicData));
                          dispatch(setIsPlaying(true));
                        }}
                        size={18}
                      />
                    )}
                  </>
                ) : (
                  <Upload
                    onClick={() => {
                      setIsDialogUploadSoundMusic(true);
                    }}
                    size={18}
                  />
                )}
              </button>

              {musicData?.approved === "Yes" ? (
                <></>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (musicData?.approved === "No") {
                      setIsDialogShareSendReviewMusicCopyright(true);
                    }
                  }}
                  className={`flex items-center justify-center px-2 pb-1 py-1 text-xs rounded-md text-white ${initStatusColors(
                    musicData?.approved
                  )} `}
                >
                  <p>
                    {musicData?.approved === "Yes"
                      ? ""
                      : musicData?.approved === "No"
                      ? "Send Review Request"
                      : musicData?.approved}
                  </p>
                </button>
              )}

              <button
                onClick={(e) => {
                  handleShareClick(e, musicData?.shared);
                }}
                className={`flex items-center justify-center px-2 py-1 text-xs rounded-sm text-white  ${
                  musicData?.shared === "Yes"
                    ? "bg-green-600"
                    : musicData?.shared === "In Review"
                    ? "bg-yellow-500"
                    : "bg-blue-600"
                } `}
              >
                <p>
                  {musicData?.shared === "Yes"
                    ? "Shared"
                    : musicData?.shared === "In Review"
                    ? musicData?.shared
                    : "Share"}
                </p>
              </button>
            </div>

            <div>
              <span className="text-destructive text-lg dark:text-white font-semibold">
                {musicData?.commercial_price}
              </span>
            </div>
          </div>
        </div>
      </div>

      <DialogUploadSoundMusic
        isDialogOpen={isDialogUploadSoundMusic}
        setIsDialogOpen={setIsDialogUploadSoundMusic}
        soundData={musicData}
        onUploadSuccess={(valueSuccess) => {
          musicData = valueSuccess;
          setMusicDatas((prevState: any) => ({
            ...prevState,
            songs: {
              ...prevState.songs,
              data: prevState?.songs?.data?.map((el: any) =>
                el.id === valueSuccess.id ? { ...el, ...valueSuccess } : el
              ),
            },
          }));
        }}
      />

      <DialogTrimMusic
        isDialogOpen={isDialogTrimMusic}
        setIsDialogOpen={setIsDialogTrimMusic}
        soundData={musicData}
        onUploadSuccess={(valueSuccess) => {
          // setMusicDatas((prevState: any) => ({
          //   ...prevState,
          //   songs: {
          //     ...prevState.songs,
          //     data: prevState?.songs?.data?.map((el: any) =>
          //       el.id === valueSuccess.id ? { ...el, ...valueSuccess } : el
          //     ),
          //   },
          // }));
        }}
      />

      <DialogShareReviewMusic
        isDialogOpen={isDialogShareMusic}
        setIsDialogOpen={setIsDialogShareMusic}
        soundData={musicData}
        onUploadSuccess={(valueSuccess) => {
          musicData = valueSuccess;
          setMusicDatas((prevState: any) => ({
            ...prevState,
            songs: {
              ...prevState.songs,
              data: prevState?.songs?.data?.map((el: any) =>
                el.id === valueSuccess.id ? { ...el, ...valueSuccess } : el
              ),
            },
          }));
        }}
      />

      <DialogSendReviewMusicCopyright
        isDialogOpen={isDialogShareSendReviewMusicCopyright}
        setIsDialogOpen={setIsDialogShareSendReviewMusicCopyright}
        soundData={musicData}
        onUploadSuccess={(valueSuccess) => {
          musicData = valueSuccess;
          setMusicDatas((prevState: any) => ({
            ...prevState,
            songs: {
              ...prevState.songs,
              data: prevState?.songs?.data?.map((el: any) =>
                el.id === valueSuccess.id ? { ...el, ...valueSuccess } : el
              ),
            },
          }));
        }}
      />

      <DialogSendSharedMusic
        isDialogOpen={isDialogSendSharedMusic}
        setIsDialogOpen={setIsDialogSendSharedMusic}
        soundData={musicData}
        onUploadSuccess={(valueSuccess) => {
          musicData = valueSuccess;
          setMusicDatas((prevState: any) => ({
            ...prevState,
            songs: {
              ...prevState.songs,
              data: prevState?.songs?.data?.map((el: any) =>
                el.id === valueSuccess.id ? { ...el, ...valueSuccess } : el
              ),
            },
          }));
        }}
      />
    </Card>
  );
}
